<script setup>
  import { computed } from 'vue';

  import CredereHeader from './Header.vue';

  const props = defineProps({
    title: {
      type: String,
      required: true,
    },
    sizeless: {
      type: Boolean,
      default: false,
      required: false,
    },
    inline: {
      type: Boolean,
      default: false,
      required: false,
    },
  });

  const emit = defineEmits(['close']);

  function close() {
    emit('close');
  }

  const classes = computed(() => ({
    'credere-modal': true,
    'variant--sizeless': props.sizeless,
    'variant--inline': props.inline,
  }));
</script>

<template>
  <div
    :class="classes"
    @click.self="close"
  >
    <section class="credere-modal-content">
      <div class="credere-modal-header">
        <credere-header
          :title="title"
          class="credere-modal-title"
          size="small"
        />

        <button
          class="credere-modal-close"
          @click="close"
        >
          <font-awesome-icon icon="fa-solid fa-times" />
        </button>
      </div>

      <div class="credere-modal-scroller">
        <slot />
      </div>
    </section>
  </div>
</template>

<style lang="scss">
  .modal-enter-active,
  .modal-leave-active {
    transition: opacity .1s;

    .credere-modal-content { transition: transform .1s; transform-origin: left; }
  }

  .modal-enter-from {
    opacity: 0;

    .credere-modal-content { transform: scale(0.95) translateX(-50%) translateY(-50%); }
  }

  .modal-enter-to {
    opacity: 1;

    .credere-modal-content { transform: scale(1) translateX(-50%) translateY(-50%); }
  }

  .modal-leave-from {
    opacity: 1;

    .credere-modal-content { transform: scale(1) translateX(-50%) translateY(-50%); }
  }

  .modal-leave-to {
    opacity: 0;

    .credere-modal-content { transform: scale(0.9) translateX(-50%) translateY(-50%); }
  }

  .credere-modal {
    --background-color: var(--token-color-neutral-backdrop);

    bottom: 0;
    left: 0;
    position: fixed;
    right: 0;
    top: 0;
    z-index: var(--token-z-index-max);

    &.variant--inline {
      --background-color: transparent;

      position: static;
      z-index: auto;
    }
  }

  .credere-modal-content {
    --background-color: var(--token-color-constant-light);
    --border-radius: var(--token-border-radius-small);
    --box-shadow: var(--token-box-shadow-around-2);

    align-items: stretch;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    left: 50%;
    max-height: calc(100% - var(--token-spacing-xl) * 2);
    max-width: calc(100% - var(--token-spacing-xl) * 2);
    position: fixed;
    top: 50%;
    transform: translateX(-50%) translateY(-50%);
    width: var(--token-spacing-modal);

    @container (max-width: 768px) {
      max-height: calc(100% - var(--token-spacing-s) * 2);
      max-width: calc(100% - var(--token-spacing-s) * 2);
    }

    .variant--sizeless & {
      width: 1024px;
    }

    .variant--inline & {
      --border-color: var(--token-color-neutral-light);
      --border-style: solid;
      --border--width: var(--token-border-width-soft);

      max-height: none;
      max-width: none;
      position: static;
      transform: none;
      width: auto;
    }
  }

  .credere-modal-header {
    --margin: var(--token-spacing-giga);

    align-items: center;
    display: flex;
    flex-direction: row;
    justify-content: space-between;

    @container (max-width: 768px) {
      --margin: var(--token-spacing-xl) var(--token-spacing-s);
    }
  }

  .credere-modal-title {
    margin: 0;
  }

  .credere-modal-close {
    --border-radius: var(--token-border-radius-small);
    --font-size: var(--token-font-size-xl);
    --line-height: 1;
    --padding: var(--token-spacing-s);
    --margin: calc(var(--token-spacing-s) * -1)
              calc(var(--token-spacing-s) * -1)
              calc(var(--token-spacing-s) * -1)
              calc(var(--token-spacing-giga) - var(--token-spacing-s));

    appearance: none;
    cursor: pointer;
    outline: none;

    &:focus {
      --box-shadow: 0 0 0 var(--token-border-width-thin) var(--token-color-highlight-primary);
    }
  }

  .credere-modal-scroller {
    --padding: 0 var(--token-spacing-giga) var(--token-spacing-giga);

    flex: 1;
    overflow-y: auto;

    @container (max-width: 768px) {
      --padding: 0 var(--token-spacing-s) var(--token-spacing-s);
    }
  }
</style>
